import React, {useRef, useEffect, useCallback, Dispatch, SetStateAction} from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay, Button, useToast
} from "@chakra-ui/react";
import {useProjectsStore} from "../../store/useProjectsStore.ts";

interface DeleteProjectAlertDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProjectId: string | null;
  setSelectedProjectId: (id: string | null) => void;
}

const DeleteProjectAlertDialog: React.FC<DeleteProjectAlertDialogProps> = (
  {
    isOpen,
    onClose,
    selectedProjectId,
    setSelectedProjectId,
  }) => {
  const cancelRef = useRef<HTMLButtonElement>(null!);
  const {deleteProject} = useProjectsStore()
  const toast = useToast()

  const handleDeleteProject = useCallback(async (projectId: string) => {
    await deleteProject(projectId);
    toast({
      position: "bottom-left",
      title: "Project deleted",
      description: "The project has been successfully deleted.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });
    setSelectedProjectId(null)
  }, [deleteProject, toast, setSelectedProjectId]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      } else if (event.key === "Enter" && selectedProjectId) {
        (async () => {
          await handleDeleteProject(selectedProjectId);
          onClose();
        })();
      }
    };

    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, selectedProjectId, onClose, handleDeleteProject]);

  const confirmDeleteProject = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (selectedProjectId) {
      await handleDeleteProject(selectedProjectId);
      onClose(); // Close the dialog after deletion
    }
  };

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onClose}
    >
      <AlertDialogOverlay>
        <AlertDialogContent bg="gray.800" border="1px solid gray.700">
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color="white">
            Confirm Deletion
          </AlertDialogHeader>
          <AlertDialogBody color="gray.300">
            Are you sure you want to delete this project? This action cannot be undone.
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={onClose} bg="gray.600" color="white" _hover={{bg: "gray.500"}}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={confirmDeleteProject} ml={3}>
              Delete
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

export default DeleteProjectAlertDialog;